import { useTranslation } from "react-i18next";
import { AddIcon } from "@/components/icons/action-icons";
import "leaflet/dist/leaflet.css";

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Circle, MapContainer, Marker, Popup, TileLayer, useMapEvents } from "react-leaflet";
import { cn } from "@/utils/cn";
import { useGeolocation } from "@uidotdev/usehooks";
import { Globe, Loader2, Minus } from "lucide-react";
import L from "leaflet";
import toast from "react-hot-toast";
import { formatAccuracy, getCurrentLocation, isLocationPermissionDenied } from "@/utils/geolocation";

/* ---------------- Types ---------------- */
type LatLngTuple = [number, number];
type MapProps = {
    selectedLocation?: { lat: number; lng: number; name?: string } | null;
    radius?: number;
    onMarkerChange?: (pos: { lat: number; lng: number }) => void;
    className?: string;
    isDraggable?: boolean;
    showZoomControl?: boolean;
    isPannable?: boolean;
    fitToRadius?: boolean;
    /** `accuracy` (metres) draws the fix's uncertainty circle around the pin. */
    personLocation?: { lat: number; lng: number; name?: string; accuracy?: number | null } | null;
    distance?: number;
};

// ======================================================
// MAP TILE CONFIGURATION
// ======================================================
// Examples:
// OSM     → https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png
// Google  → https://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}
// Mapbox → https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}?access_token=YOUR_KEY

const DEFAULT_ZOOM = 14;

/** A "my location" fix vaguer than this is too rough to pin a work site with. */
const ROUGH_FIX_METERS = 50;
/** Sentinel `locationName` for a pin dropped by "use my location". */
const MY_LOCATION = "__my_location__";
const MAP_TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

const redMarkerIcon = new L.Icon({
    iconUrl: "/markers/marker-red.png",
    shadowUrl: "/markers/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

const pinMarkerIcon = new L.Icon({
    iconUrl: "/markers/marker-pin.png",
    shadowUrl: "/markers/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

/* ---------------- Draggable Marker ---------------- */
const DraggableMarker = memo(
    ({
        position,
        onDragEnd,
        locationName,
        isDraggable,
    }: {
        position: LatLngTuple;
        onDragEnd: (pos: LatLngTuple) => void;
        locationName: string;
        isDraggable: boolean;
    }) => {
        return (
            <Marker
                draggable={isDraggable}
                position={position}
                icon={redMarkerIcon}
                eventHandlers={{
                    dragend: (e) => {
                        const marker = e.target as L.Marker;
                        const { lat, lng } = marker.getLatLng();
                        onDragEnd([lat, lng]);
                    },
                }}
            >
                <Popup>{locationName}</Popup>
            </Marker>
        );
    }
);

/* ---------------- Single Click Handler ---------------- */

const MapClickHandler = ({ onClick }: { onClick: (lat: number, lng: number) => void }) => {
    useMapEvents({
        click(e) {
            onClick(e.latlng.lat, e.latlng.lng);
        },
    });
    return null;
};

/* ---------------- Main Component ---------------- */

const MapviewContainer = ({ selectedLocation, personLocation, radius = 200, onMarkerChange, className, isDraggable = true, showZoomControl, isPannable, fitToRadius = false, distance }: MapProps) => {
    const { t } = useTranslation(["common"]);
    const zoomEnabled = showZoomControl ?? isDraggable;
    const panEnabled = isPannable ?? isDraggable;
    const mapRef = useRef<L.Map | null>(null);
    const { latitude, longitude, error: geoError, loading: geoLoading } = useGeolocation();

    const defaultCenter: LatLngTuple = useMemo(
        () => (latitude && longitude ? [latitude, longitude] : [25.276987, 55.296249]),
        [latitude, longitude]
    );

    const [markerPos, setMarkerPos] = useState<LatLngTuple | null>(
        selectedLocation ? [selectedLocation.lat, selectedLocation.lng] : null
    );

    // `null` = the generic "Pinned location"; "me" = "You are here". Resolved at
    // render so the popup follows a language switch.
    const [locationName, setLocationName] = useState<string | null>(selectedLocation?.name ?? null);
    // Accuracy of the "my location" fix the marker currently sits on; cleared
    // as soon as the marker is moved by hand.
    const [myFixAccuracy, setMyFixAccuracy] = useState<number | null>(null);
    const [isLocatingMe, setIsLocatingMe] = useState(false);

    useEffect(() => {
        if (!selectedLocation || !mapRef.current) return;

        const pos: LatLngTuple = [selectedLocation.lat, selectedLocation.lng];
        const currentZoom = mapRef.current.getZoom();
        mapRef.current.flyTo(pos, currentZoom);
        setMarkerPos(pos);
        setLocationName(selectedLocation.name ?? null);
    }, [selectedLocation]);

    useEffect(() => {
        if (!mapRef.current) return;

        const personLatLng: LatLngTuple | null = personLocation
            ? [personLocation.lat, personLocation.lng]
            : null;

        const markerLatLng: LatLngTuple | null = markerPos ?? null;

        if (!markerLatLng && !personLatLng) {
            mapRef.current.setView(defaultCenter, DEFAULT_ZOOM);
            return;
        }

        if (markerLatLng && !personLatLng) {
            if (fitToRadius && radius > 0) {
                const circleBounds = L.latLng(markerLatLng[0], markerLatLng[1]).toBounds(radius * 2);
                mapRef.current.fitBounds(circleBounds, { padding: [24, 24], animate: false });
            } else {
                mapRef.current.setView(markerLatLng, 18);
            }
            return;
        }

        if (!markerLatLng && personLatLng) {
            mapRef.current.setView(personLatLng, 18);
            return;
        }

        const bounds = L.latLngBounds([markerLatLng as LatLngTuple, personLatLng as LatLngTuple]);

        if ((distance ?? 0) === 0) {
            mapRef.current.setView(markerLatLng as LatLngTuple, 20);
        } else if ((distance ?? 0) < radius * 3) {
            const center = bounds.getCenter();
            mapRef.current.setView(center, 17, { animate: true });
        } else {
            mapRef.current.fitBounds(bounds, { padding: [80, 80], maxZoom: 16, animate: true });
        }
    }, [markerPos, personLocation, distance, radius, fitToRadius, defaultCenter]);

    const handleMarkerMove = useCallback(
        (pos: LatLngTuple) => {
            setMarkerPos(pos);
            setMyFixAccuracy(null);
            mapRef.current?.flyTo(pos);
            onMarkerChange?.({ lat: pos[0], lng: pos[1] });
        },
        [onMarkerChange]
    );

    const handleMapClick = useCallback(
        (lat: number, lng: number) => {
            const pos: LatLngTuple = [lat, lng];
            setMarkerPos(pos);
            setMyFixAccuracy(null);
            setLocationName(null);
            mapRef.current?.flyTo(pos);
            onMarkerChange?.({ lat, lng });
        },
        [onMarkerChange]
    );

    // A fresh high-accuracy fix, not the page-load reading above: that one is
    // a low-accuracy (Wi-Fi / IP) estimate, and pinning a work site with it put
    // the whole geofence wherever the estimate happened to land.
    const showMyLocation = useCallback(async () => {
        if (!mapRef.current) return;

        setIsLocatingMe(true);
        try {
            const fix = await getCurrentLocation();
            const pos: LatLngTuple = [fix.lat, fix.long];
            setMarkerPos(pos);
            setMyFixAccuracy(fix.accuracy);
            setLocationName(MY_LOCATION);
            mapRef.current?.flyTo(pos, Math.max(mapRef.current.getZoom(), 17));
            onMarkerChange?.({ lat: fix.lat, lng: fix.long });
        } catch (error) {
            toast.error(
                isLocationPermissionDenied(error)
                    ? t("common:map.location_blocked")
                    : t("common:map.location_failed")
            );
        } finally {
            setIsLocatingMe(false);
        }
    }, [onMarkerChange, t]);

    const myFixLabel = formatAccuracy(myFixAccuracy);
    const personAccuracy = personLocation?.accuracy;

    const center = markerPos ?? defaultCenter;
    const markerLabel =
        locationName === MY_LOCATION
            ? t("common:map.you_are_here")
            : (locationName ?? t("common:map.pinned_location"));

    return (
        <div className={cn("h-120 rounded-md relative overflow-hidden z-10", className)}>
            <MapContainer
                center={center}
                zoom={DEFAULT_ZOOM}
                dragging={panEnabled}
                scrollWheelZoom={zoomEnabled}
                touchZoom={zoomEnabled}
                doubleClickZoom={isDraggable}
                boxZoom={isDraggable}
                keyboard={isDraggable}
                zoomControl={false}
                style={{ height: "100%", width: "100%" }}
                ref={mapRef}
            >
                <TileLayer url={MAP_TILE_URL} />

                {isDraggable && <MapClickHandler onClick={handleMapClick} />}

                {markerPos && (
                    <>
                        <DraggableMarker isDraggable={isDraggable} position={markerPos} locationName={markerLabel} onDragEnd={handleMarkerMove} />

                        {radius > 0 && (
                            <Circle
                                center={markerPos}
                                radius={radius}
                                pathOptions={{
                                    color: "var(--color-info)",
                                    weight: 1,
                                    dashArray: "4, 4",
                                    fillOpacity: 0.25,
                                }}
                            />
                        )}
                        {myFixAccuracy != null && myFixAccuracy > 0 && (
                            <Circle
                                center={markerPos}
                                radius={myFixAccuracy}
                                pathOptions={{
                                    color: "var(--color-warning)",
                                    weight: 1,
                                    fillOpacity: 0.12,
                                }}
                            />
                        )}
                    </>
                )}
                {/* The fix's uncertainty: the employee is somewhere in here, not
                    necessarily at the pin. */}
                {personLocation && personAccuracy != null && personAccuracy > 0 && (
                    <Circle
                        center={[personLocation.lat, personLocation.lng]}
                        radius={personAccuracy}
                        pathOptions={{
                            color: "var(--color-warning)",
                            weight: 1,
                            fillOpacity: 0.12,
                        }}
                    />
                )}
                {personLocation && (
                    <Marker
                        position={[personLocation.lat, personLocation.lng]}
                        icon={pinMarkerIcon}
                        draggable={false}
                    >
                        <Popup>{personLocation?.name}</Popup>
                    </Marker>
                )}
            </MapContainer>

            {isDraggable && (geoLoading || geoError || !markerPos) && (!personLocation) && (
                <div className="absolute bottom-4 end-4 z-1000 flex flex-col gap-2">
                    {geoLoading && (
                        <div className="bg-white px-3 py-2 rounded-md shadow-lg flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                            <span className="text-sm text-gray-700">{t("common:map.locating")}</span>
                        </div>
                    )}
                    {!geoLoading && geoError && (
                        <div className="bg-red-50 border border-red-200 px-3 py-2 rounded-md shadow-lg">
                            <span className="text-sm text-red-700">{t("common:map.location_unavailable")}</span>
                        </div>
                    )}
                </div>
            )}

            {isDraggable && (
                <div className="absolute bottom-4 start-4 z-1000 flex items-end gap-2">
                    <button
                        type="button"
                        onClick={showMyLocation}
                        disabled={isLocatingMe}
                        aria-label={t("common:map.use_my_location")}
                        title={t("common:map.use_my_location")}
                        className="bg-white p-2 rounded-lg shadow-md border border-gray-100 hover:bg-gray-50 transition-colors disabled:cursor-wait"
                    >
                        {isLocatingMe ? (
                            <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
                        ) : (
                            <Globe className="w-4 h-4 text-gray-600" />
                        )}
                    </button>
                    {myFixLabel && (
                        <p className="max-w-60 rounded-md bg-white px-2 py-1 text-[11px] text-gray-600 shadow-md">
                            {myFixAccuracy! > ROUGH_FIX_METERS
                                ? t("common:map.accuracy_rough", { accuracy: myFixLabel })
                                : t("common:map.accuracy", { accuracy: myFixLabel })}
                        </p>
                    )}
                </div>
            )}

            {zoomEnabled && (
                <div className="absolute top-3 end-3 z-1000 flex flex-col rounded-lg overflow-hidden shadow-md border border-gray-100">
                    <button
                        type="button"
                        onClick={() => mapRef.current?.zoomIn()}
                        className="bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors w-8 h-8 flex items-center justify-center border-b border-gray-100"
                        aria-label={t("studio:workflows.zoom_in")}
                    >
                        <AddIcon className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                    <button
                        type="button"
                        onClick={() => mapRef.current?.zoomOut()}
                        className="bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors w-8 h-8 flex items-center justify-center"
                        aria-label={t("studio:workflows.zoom_out")}
                    >
                        <Minus className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default memo(MapviewContainer);
